import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["backable", "cancellable", "defaultBackButton", "defaultBackLabel", "defaultCancelButton", "defaultCancelLabel", "defaultDoneButton", "defaultDoneLabel", "defaultHeading", "defaultNextButton", "defaultNextLabel", "disablePrimaryButton", "footerComponent", "headerComponent", "onConfirm", "onReject", "children", "onStepIndexChange", "stepIndex", "stickyHeader", "headerdatatestid", "primaryButtonTooltip", "primaryButtonTooltipProps"];
import { Children, isValidElement, useEffect, useMemo, useRef } from 'react';
import { syntheticEvent } from '../../utils/SyntheticEvent';
import { Button } from '../Button';
import { Controllable } from '../Controllable/Controllable';
import { WizardHeader } from './WizardHeader';
import { WizardFooter } from './WizardFooter';
import styled, { useTheme } from 'foundations-theming/utils';
import { WizardProvider } from './WizardProvider';
import { callIfPossible } from '../../utils/functions';
import unescapedText from 'I18n/utils/unescapedText';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledWizard = styled.div.withConfig({
  displayName: "Wizard__StyledWizard",
  componentId: "sc-5plj9p-0"
})(({
  $cssStyles
}) => {
  return Object.assign({
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 0%',
    maxHeight: '100%'
  }, $cssStyles);
});
const StyledContentWrapper = styled.div.withConfig({
  displayName: "Wizard__StyledContentWrapper",
  componentId: "sc-5plj9p-1"
})(({
  $stickyHeader
}) => {
  return Object.assign({
    overflow: 'auto',
    flex: '1 1 0%'
  }, $stickyHeader && {
    display: 'flex',
    flexDirection: 'column'
  });
});
export const WizardComponent = _ref => {
  let {
      backable = true,
      cancellable = false,
      defaultBackButton = Button,
      defaultBackLabel = unescapedText('salesUI.UIWizard.back'),
      defaultCancelButton = Button,
      defaultCancelLabel = unescapedText('salesUI.UIWizard.cancel'),
      defaultDoneButton = Button,
      defaultDoneLabel = unescapedText('salesUI.UIWizard.done'),
      defaultHeading,
      defaultNextButton = Button,
      defaultNextLabel = unescapedText('salesUI.UIWizard.next'),
      disablePrimaryButton = false,
      footerComponent = WizardFooter,
      headerComponent = WizardHeader,
      onConfirm,
      onReject,
      children,
      onStepIndexChange,
      stepIndex = 0,
      stickyHeader = true,
      headerdatatestid,
      primaryButtonTooltip,
      primaryButtonTooltipProps
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const theme = useTheme();
  const wizardRef = useRef(null);
  const Footer = footerComponent; // JSX needs a capitalized var name!
  const Header = headerComponent; // JSX needs a capitalized var name!

  const {
    currentStep,
    stepProps,
    isFirstStep,
    isLastStep
  } = useMemo(() => {
    const steps = Children.toArray(children);
    const current = steps[stepIndex];
    const propsForStep = /*#__PURE__*/isValidElement(current) ? current.props : {};
    return {
      currentStep: current,
      stepProps: propsForStep,
      isFirstStep: stepIndex === 0 || propsForStep.isFirstStep,
      isLastStep: stepIndex === steps.length - 1 || propsForStep.isLastStep
    };
  }, [children, stepIndex]);
  const wizardProps = {
    backable,
    cancellable,
    defaultBackButton,
    defaultBackLabel,
    defaultCancelButton,
    defaultCancelLabel,
    defaultDoneButton,
    defaultDoneLabel,
    defaultHeading,
    defaultNextButton,
    defaultNextLabel,
    disablePrimaryButton,
    footerComponent,
    headerComponent,
    onConfirm,
    onReject,
    children,
    onStepIndexChange,
    stepIndex,
    stickyHeader,
    headerdatatestid,
    primaryButtonTooltip,
    primaryButtonTooltipProps
  };
  useEffect(() => {
    if (!stickyHeader && wizardRef.current) {
      wizardRef.current.scrollTop = 0;
    }
  }, [stepIndex, stickyHeader]);
  const handleClickBack = () => callIfPossible(onStepIndexChange, syntheticEvent(stepIndex - 1));
  const handleClickNext = () => callIfPossible(onStepIndexChange, syntheticEvent(stepIndex + 1));
  const {
    domProps,
    tokenProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  return /*#__PURE__*/_jsx(WizardProvider, {
    dialogBodyStyleOverrides: {
      paddingBlock: theme.space['1000']
    },
    children: /*#__PURE__*/_jsxs(StyledWizard, Object.assign({}, domProps, {
      $cssStyles: cssStyles,
      children: [/*#__PURE__*/_jsxs(StyledContentWrapper, {
        $stickyHeader: stickyHeader,
        children: [Header !== null ? /*#__PURE__*/_jsx(Header, {
          stepProps: stepProps,
          wizardProps: wizardProps,
          headerdatatestid: headerdatatestid
        }) : null, currentStep]
      }), /*#__PURE__*/_jsx(Footer, {
        backButton: stepProps.backButton || defaultBackButton,
        backLabel: stepProps.backLabel || defaultBackLabel,
        cancelButton: stepProps.cancelButton || defaultCancelButton,
        cancelLabel: stepProps.cancelLabel || defaultCancelLabel,
        doneButton: stepProps.doneButton || defaultDoneButton,
        doneLabel: stepProps.doneLabel || defaultDoneLabel,
        hidePrimaryControls: stepProps.hidePrimaryControls,
        hideSecondaryControls: stepProps.hideSecondaryControls,
        isFirstStep: isFirstStep,
        isLastStep: isLastStep,
        nextButton: stepProps.nextButton || defaultNextButton,
        nextLabel: stepProps.nextLabel || defaultNextLabel,
        onClickBack: handleClickBack,
        onClickCancel: onReject,
        onClickDone: onConfirm,
        onClickNext: handleClickNext,
        stepIndex: stepIndex,
        stepProps: stepProps,
        wizardProps: wizardProps
      })]
    }))
  });
};
WizardComponent.displayName = 'Wizard';
export const Wizard = Controllable(WizardComponent, ['stepIndex']);